import { useState, useEffect, useRef } from 'react';
import { getReferenceOptions } from '@shared/services/services';

export const emptyOptions = { city: [], hotel: [] };

export const useReferenceSearch = ({
  itemsPerPage = 5,
  setLodgingSelection,
} = {}) => {
  const [searchText, setSearchText] = useState('');
  const [allOptions, setAllOptions] = useState(emptyOptions);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [isEditing, setIsEditing] = useState(false);

  const lastSearchedTextRef = useRef('');
  const cacheRef = useRef({});

  const normalizeString = str =>
    (str ? String(str) : '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[,.-]/g, ' ')
      .toLowerCase();

  const compactSpaces = str => str.replace(/\s+/g, ' ').trim();

  const fetchOptions = async text => {
    const searchFormatted = compactSpaces(normalizeString(text));

    if (!searchFormatted) return;

    const cachedKeys = Object.keys(cacheRef.current);

    const cachedMatchKey = cachedKeys.find(
      key => key.startsWith(searchFormatted) || searchFormatted.startsWith(key)
    );

    if (cachedMatchKey) {
      setAllOptions(cacheRef.current[cachedMatchKey]);
      setLoading(false);
      return;
    }

    if (searchFormatted === lastSearchedTextRef.current) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      lastSearchedTextRef.current = searchFormatted;

      const response = await getReferenceOptions(searchFormatted);

      const cities = response?.cities || [];
      const hotels = response?.hotels || [];

      const newResults = {
        city: cities,
        hotel: hotels,
      };

      if (cities.length > 0 || hotels.length > 0) {
        cacheRef.current[searchFormatted] = newResults;
      }

      setAllOptions(newResults);
    } catch (error) {
      console.error('Error fetching options:', error);

      setAllOptions(emptyOptions);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchText.length >= 3) {
        fetchOptions(searchText);
        setIsOpen(true);
      } else {
        setIsOpen(false);
        setAllOptions(emptyOptions);
      }
    }, 400);

    return () => clearTimeout(handler);
  }, [searchText]);

  const filterOptions = options => {
    if (searchText.length < 3) return [];

    const normalizedSearch = compactSpaces(normalizeString(searchText));

    return options
      .filter(option =>
        compactSpaces(normalizeString(option?.option?.label)).includes(
          normalizedSearch
        )
      )
      .slice(0, itemsPerPage);
  };

  const groupedOptions = [
    {
      group: 'city',
      items: filterOptions(allOptions.city),
    },
    {
      group: 'hotel',
      items: filterOptions(allOptions.hotel),
    },
  ].filter(g => g.items.length > 0);

  const highlightMatch = (text, highlight) => {
    if (!highlight) {
      return [
        {
          text,
          highlight: false,
          key: 0,
        },
      ];
    }

    const normalizedText = normalizeString(text);

    const normalizedHighlight = compactSpaces(normalizeString(highlight));

    const pattern = normalizedHighlight
      .split(/\s+/)
      .map(word => String.raw`\w*${word}\w*`)
      .join(String.raw`\s+`);

    const regex = new RegExp(`(${pattern})`, 'gi');

    const normalizedParts = normalizedText.split(regex);

    let currentIndex = 0;

    return normalizedParts.map((part, index) => {
      const partLength = part.length;

      const originalPart = text.substring(
        currentIndex,
        currentIndex + partLength
      );

      currentIndex += partLength;

      const isHighlighted = compactSpaces(normalizeString(part)).includes(
        normalizedHighlight
      );

      if (isHighlighted) {
        return {
          text: originalPart,
          highlight: true,
          key: index,
        };
      }

      return {
        text: originalPart,
        highlight: false,
        key: index,
      };
    });
  };

  const resetSearch = () => {
    setIsEditing(false);
    setSearchText('');
    setAllOptions(emptyOptions);
    setIsOpen(false);
    setLodgingSelection(null);
    lastSearchedTextRef.current = '';
  };

  const handleSelect = item => {
    setLodgingSelection(item);
    setIsEditing(false);
    setSearchText('');
    setIsOpen(false);
    setAllOptions(emptyOptions);
  };

  return {
    searchText,
    setSearchText,
    allOptions,
    groupedOptions,
    loading,
    isOpen,
    setIsOpen,
    highlightMatch,
    resetSearch,
    handleSelect,
    isEditing,
    setIsEditing,
  };
};
